// VFX clocks and the photosensitivity limits every effect obeys (docs/ART-DIRECTION.md §4: nothing flashes faster
// than 3 Hz, glitch bursts are local and short).

/**
 * Glitch jitter re-rolls this many times a second: a texel can toggle at most once per step, so the fastest on/off
 * cycle is steps / 2 = 3 Hz.
 */
export const GLITCH_STEPS_PER_SEC = 6
/** "Damage static" re-rolls its speckle pattern this often (≤ 2 Hz on/off per texel). */
export const STATIC_STEPS_PER_SEC = 4
/** Smooth sine alpha flicker (paper leaflets), under 3 Hz. */
export const FLICKER_HZ = 2
/**
 * Shader clock wrap. Holds whole dash-scroll cycles for every scroll rate in the effect table (rates are multiples of
 * 1/60 cycles per second), so wrapping is seamless.
 */
export const SHADER_TIME_WRAP_SEC = 60

export const TAU = Math.PI * 2

/** ART-DIRECTION §4: flash onsets allowed in any one-second window, screen-wide (3 Hz). */
export const MAX_FLASHES_PER_SEC = 3
const FLASH_WINDOW_SEC = 1
/** Remembered onsets: covers every onset that can still share a window with a new one while flash delays stay < 1 s. */
const FLASH_MEMORY = MAX_FLASHES_PER_SEC * 3

/**
 * Screen-wide flash budget. Each effect alone stays under 3 Hz, but several impacts landing together could flash
 * faster, so a flash is admitted only while fewer than MAX_FLASHES_PER_SEC admitted onsets lie within one window
 * of its own: no one-second window then ever holds more. Screen-wide is stricter than any per-region budget, so
 * it holds wherever the camera looks. A refused flash is not played; the rest of its event's effects still are.
 */
export class FlashBudget {
  private readonly onsets = new Float64Array(FLASH_MEMORY).fill(Number.NEGATIVE_INFINITY)

  /** Admits (and records) a flash starting at `onset` on the layer clock, or refuses it. Allocation-free. */
  admit(onset: number): boolean {
    const onsets = this.onsets
    let near = 0
    let oldest = 0
    for (let i = 0; i < onsets.length; i++) {
      const other = onsets[i] as number
      if (Math.abs(other - onset) < FLASH_WINDOW_SEC) near++
      if (other < (onsets[oldest] as number)) oldest = i
    }
    if (near >= MAX_FLASHES_PER_SEC) return false
    onsets[oldest] = onset
    return true
  }
}

/** Attack/decay shape of an effect's brightness over its normalized age t ∈ [0, 1]. */
export interface Envelope {
  /** Fraction of the lifetime spent fading in. */
  readonly attack: number
  /** Exponent of the (1 − t) fade-out; higher = faster early fade. */
  readonly decay: number
}

export function envelopeAt(envelope: Envelope, t: number): number {
  const rise = envelope.attack > 0 ? Math.min(1, t / envelope.attack) : 1
  return rise * Math.pow(Math.max(0, 1 - t), envelope.decay)
}

/** Quadratic ease-out: fast start, gentle arrival. */
export function easeOut(t: number): number {
  const inverse = 1 - t
  return 1 - inverse * inverse
}

/** Head position along a trail's path at normalized age `t`: the def's head range, eased out. Shared by the CPU path trails and the shader-clipped route sweeps. */
export function trailHead(head: readonly [min: number, max: number], t: number): number {
  return head[0] + (head[1] - head[0]) * easeOut(t)
}

/** The tail behind `head`, as a path fraction (never before the path start). */
export function trailTail(head: number, tail: number): number {
  return Math.max(0, head - tail)
}
